import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Zone, ZoneInsert, ZoneUpdate } from '@/types/zone';
import { toast } from 'sonner';

interface ZoneState {
  zones: Zone[];
  loading: boolean;

  loadZones: () => Promise<void>;
  addZone: (zone: ZoneInsert) => Promise<Zone | null>;
  updateZone: (id: string, updates: ZoneUpdate) => Promise<void>;
  deleteZone: (id: string) => Promise<void>;
  getZoneById: (id: string) => Zone | undefined;
  getZonesBySite: (siteId: string) => Zone[];
}

export const useZoneStore = create<ZoneState>()((set, get) => ({
  zones: [],
  loading: false,

  loadZones: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('zones')
        .select('*')
        .order('name');

      if (error) throw error;
      set({ zones: (data || []) as Zone[] });
    } catch (error) {
      console.error('Error loading zones:', error);
      toast.error('Failed to load zones');
    } finally {
      set({ loading: false });
    }
  },

  addZone: async (zone) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const insertData: any = {
        name: zone.name,
        domain: zone.domain,
        org_id: profile.org_id,
        created_by: userData.user.id,
        updated_by: userData.user.id,
      };

      // Add optional fields only if defined
      if (zone.site_id) insertData.site_id = zone.site_id;
      if (zone.cloud_service_id) insertData.cloud_service_id = zone.cloud_service_id;
      if (zone.purdue_level) insertData.purdue_level = zone.purdue_level;
      if (zone.cidrs) insertData.cidrs = zone.cidrs;
      if (zone.criticality) insertData.criticality = zone.criticality;
      if (zone.data_classification) insertData.data_classification = zone.data_classification;
      if (zone.backup_frequency) insertData.backup_frequency = zone.backup_frequency;
      if (zone.description) insertData.description = zone.description;
      if (zone.cloud_metadata) insertData.cloud_metadata = zone.cloud_metadata;

      const { data, error } = await supabase
        .from('zones')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      set(state => ({ zones: [...state.zones, data as Zone] }));
      toast.success('Zone created');
      return data as Zone;
    } catch (error) {
      console.error('Error creating zone:', error);
      toast.error('Failed to create zone');
      return null;
    }
  },

  updateZone: async (id, updates) => {
    try {
      const updateData = {
        ...updates,
        cloud_metadata: updates.cloud_metadata as any,
      };

      const { error } = await supabase
        .from('zones')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        zones: state.zones.map(z => z.id === id ? { ...z, ...updates } : z)
      }));
      toast.success('Zone updated');
    } catch (error) {
      console.error('Error updating zone:', error);
      toast.error('Failed to update zone');
    }
  },

  deleteZone: async (id) => {
    try {
      const { error } = await supabase
        .from('zones')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        zones: state.zones.filter(z => z.id !== id)
      }));
      toast.success('Zone deleted');
    } catch (error) {
      console.error('Error deleting zone:', error);
      toast.error('Failed to delete zone');
    }
  },

  getZoneById: (id) => {
    return get().zones.find(z => z.id === id);
  },

  getZonesBySite: (siteId) => {
    return get().zones.filter(z => z.site_id === siteId);
  },
}));
